import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowsLeftRight, Bank, Briefcase, Buildings, CalendarBlank, CalendarX, DeviceMobile, EnvelopeSimple, Hash, IdentificationCard,
  MapPin, MapTrifold, NotePencil, Phone, Signpost, User, Users,
} from '@phosphor-icons/react';
import {
  Chip, FeldRaster, LivePreviewCard, PersonAvatar, SchalterZeile, SearchSelect, SegmentedControl, StepForm, Stepper, TextFeld, ToastPill, useToastPill,
} from '../../../../components/immobilien/ui';
import { eur, initialen } from '../../../../lib/immobilien/format';
import { deDatum } from '../../../../lib/immobilien/objektDetail';
import type { ObjektOption } from '../../../../lib/immobilien/objektOptionen';
import type { EigListeDaten } from '../../../../lib/eigentuemer/useEigentuemerListe';
import {
  EIG_ANREDEN, LEERER_EIG_ENTWURF, ROLLEN_TEXT, anzeigeName, belegung, einheitenMitEigentum, feldFehler, gewaehlteRollen, hinweis, mitVerguetung,
  objektArtAus, objektBesitzer, rollenOptionen, schreibPlan, schritteErledigt, wechselText, type NeuerEigentuemerEntwurf,
} from '../../../../lib/eigentuemer/neuerEigentuemer';
import { legeEigentuemerAn } from '../../../../lib/eigentuemer/neuerEigentuemerSpeichern';

const KACHEL = (an: boolean): React.CSSProperties => ({
  borderRadius: 14, padding: '10px 12px', gap: 2, border: 'none', color: 'var(--ink)',
  background: an ? 'var(--acc-soft)' : 'var(--bg)', boxShadow: an ? 'inset 0 0 0 2px var(--acc)' : 'inset 0 0 0 1px var(--line)',
});

/**
 * Rückseite der Drehkarte „Neuer Eigentümer“ (Handoff Eigentümer §6.4, Screens 08/09/62):
 * Person · Objekt & Einheiten · Verwaltervertrag & Bank, rechts die Live-Vorschau.
 */
export function NeuerEigentuemerFormular({ daten, objekte, heute, onGeaendert, onGespeichert }: {
  daten: EigListeDaten;
  objekte: readonly ObjektOption[];
  heute: string;
  onGeaendert: (geaendert: boolean) => void;
  onGespeichert: (id: string, meldung: string) => void;
}) {
  const [e, setE] = useState<NeuerEigentuemerEntwurf>(LEERER_EIG_ENTWURF);
  const [speichert, setSpeichert] = useState(false);
  const [toast, zeige] = useToastPill();
  const setze = <K extends keyof NeuerEigentuemerEntwurf>(k: K) => (v: NeuerEigentuemerEntwurf[K]) => setE(alt => ({ ...alt, [k]: v }));

  useEffect(() => { onGeaendert(JSON.stringify(e) !== JSON.stringify(LEERER_EIG_ENTWURF)); }, [e, onGeaendert]);

  const objekt = e.objektId ? objekte.find(o => o.id === e.objektId) ?? null : null;
  const art = objektArtAus(daten.objekte, e.objektId);
  const einheiten = useMemo(() => (e.objektId ? einheitenMitEigentum(e.objektId, art, daten, heute) : []), [daten, e.objektId, art, heute]);
  const objektEig = useMemo(() => (e.objektId ? objektBesitzer(e.objektId, daten, heute) : []), [daten, e.objektId, heute]);
  const b = useMemo(() => belegung(art, einheiten, e.einheitIds, objektEig), [art, einheiten, e.einheitIds, objektEig]);
  const belegt = b.vorbesitzer.length > 0;
  const rollen = gewaehlteRollen(art, e.sev);
  const firma = e.anrede === 'Firma';

  const [s1, s2, s3] = schritteErledigt(e, einheiten.length);
  const h = hinweis(e, einheiten.length);
  const fehler = feldFehler(e);
  const name = s1 ? anzeigeName(e) : 'Neuer Eigentümer';
  const alle = einheiten.length > 0 && einheiten.every(x => e.einheitIds.includes(x.id));
  // Vergütung je verwalteter Einheit (ganzes Objekt ohne angelegte Einheiten zählt als eine)
  const anzahlVerwaltet = einheiten.length === 0 ? 1 : e.einheitIds.length;
  const verguetungMonat = mitVerguetung(rollen) ? Math.round(e.verguetung * anzahlVerwaltet * 100) / 100 : 0;
  const umfang = !objekt ? 'Kein Objekt gewählt'
    : `${objekt.label} · ${einheiten.length === 0 || alle ? 'ganzes Objekt' : `${e.einheitIds.length} ${e.einheitIds.length === 1 ? 'Einheit' : 'Einheiten'}`}`;

  const waehleObjekt = (id: string) => {
    const a = objektArtAus(daten.objekte, id);
    // Mietverwaltung: ganzes Objekt vorgewählt; WEG: Einheiten einzeln wählen (Prototyp)
    const vor = a === 'MV' ? daten.einheiten.filter(x => x.objekt_id === id).map(x => x.id) : [];
    setE(alt => ({ ...alt, objektId: id, einheitIds: vor, art: 'wechsel', sev: false }));
  };
  const schalteEinheit = (id: string) => setE(alt => ({
    ...alt, einheitIds: alt.einheitIds.includes(id) ? alt.einheitIds.filter(x => x !== id) : [...alt.einheitIds, id],
  }));
  const schalteAlle = () => setE(alt => ({ ...alt, einheitIds: alle ? [] : einheiten.map(x => x.id) }));

  const speichere = async () => {
    const plan = schreibPlan(e, art, einheiten, b, daten.einheitEig, heute);
    if (!plan) return;
    setSpeichert(true);
    try {
      const r = await legeEigentuemerAn(e, plan, rollen);
      const teile = [`${anzeigeName(e)} angelegt`];
      if (belegt && e.art === 'wechsel') teile.push(`Wechsel ${b.vorbesitzer.map(v => v.name).join(', ')} zum ${deDatum(e.beginn)} vorgemerkt`);
      teile.push(...r.warnungen);
      onGespeichert(r.id, teile.join(' · '));
    } catch (err) {
      zeige(`Nicht gespeichert: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`, true);
    } finally {
      setSpeichert(false);
    }
  };

  const schritte = [
    {
      titel: 'Person',
      untertitel: 'Persönliche Stammdaten',
      erledigt: s1,
      inhalt: (
        <>
          <SegmentedControl ariaLabel="Anrede" hoehe={34} wert={e.anrede} onChange={setze('anrede')} segmente={EIG_ANREDEN.map(a => ({ key: a, label: a }))} className="self-start" />
          <FeldRaster minBreite={170}>
            {firma && <TextFeld label="Firmenname" icon={Buildings} wert={e.firma} onChange={setze('firma')} placeholder="z. B. Muster Immobilien GmbH" autoComplete="off" voll />}
            <TextFeld label={firma ? 'Ansprechpartner Vorname' : 'Vorname'} icon={User} wert={e.vorname} onChange={setze('vorname')} placeholder="Vorname" autoComplete="off" />
            <TextFeld label={firma ? 'Ansprechpartner Nachname' : 'Nachname'} icon={User} wert={e.nachname} onChange={setze('nachname')} placeholder="Nachname" autoComplete="off" />
            <TextFeld label="E-Mail" icon={EnvelopeSimple} type="email" wert={e.email} onChange={setze('email')} placeholder="name@beispiel.de" autoComplete="off" fehler={fehler.email} />
            <TextFeld label="Telefon" icon={Phone} type="tel" wert={e.telefon} onChange={setze('telefon')} placeholder="06421 …" autoComplete="off" />
            <TextFeld label="Straße" icon={Signpost} wert={e.strasse} onChange={setze('strasse')} placeholder="Musterstraße" autoComplete="off" />
            <TextFeld label="Nr." icon={Hash} wert={e.hausnummer} onChange={setze('hausnummer')} placeholder="1" autoComplete="off" />
            <TextFeld label="PLZ" icon={MapPin} inputMode="numeric" wert={e.plz} onChange={setze('plz')} placeholder="35037" autoComplete="off" fehler={fehler.plz} />
            <TextFeld label="Stadt" icon={MapTrifold} wert={e.stadt} onChange={setze('stadt')} placeholder="Marburg" autoComplete="off" />
          </FeldRaster>
        </>
      ),
    },
    {
      titel: 'Objekt & Einheiten',
      untertitel: 'Objekt suchen und Eigentum zuordnen (Mehrfachauswahl)',
      erledigt: s2,
      inhalt: (
        <>
          <SearchSelect
            optionen={objekte}
            ausgewaehlt={objekt}
            onWahl={o => waehleObjekt(o.id)}
            onEntfernen={() => setE(alt => ({ ...alt, objektId: null, einheitIds: [], sev: false }))}
            platzhalter="Objekt suchen …"
            maxTreffer={8}
          />
          {objekt && einheiten.length === 0 && (
            <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Für dieses Objekt sind keine Einheiten angelegt – Zuordnung zum ganzen Objekt.</span>
          )}
          {objekt && einheiten.length > 0 && (
            <div role="group" aria-label="Einheiten" className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8 }}>
              <button type="button" aria-pressed={alle} onClick={schalteAlle} className="flex flex-col text-left cursor-pointer min-w-0" style={KACHEL(alle)}>
                <span className="truncate" style={{ fontSize: 13, fontWeight: 800 }}>Ganzes Objekt</span>
                <span className="truncate" style={{ fontSize: 11, fontWeight: 700, color: 'var(--acc)' }}>{einheiten.length} Einheiten</span>
              </button>
              {einheiten.map(x => {
                const an = e.einheitIds.includes(x.id);
                const besitz = x.anzeige.filter(v => v.name).map(v => v.name).join(', ');
                return (
                  <button key={x.id} type="button" aria-pressed={an} onClick={() => schalteEinheit(x.id)} className="flex flex-col text-left cursor-pointer min-w-0" style={KACHEL(an)}>
                    <span className="truncate" style={{ fontSize: 13, fontWeight: 800 }}>{x.bezeichnung}</span>
                    <span className="truncate" title={besitz || undefined} style={{ fontSize: 11, fontWeight: 700, color: besitz ? 'var(--warn)' : 'var(--ok)' }}>
                      {besitz ? `Eigentum · ${besitz}` : 'ohne Eigentümer'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {belegt && (
            <div className="flex flex-col" style={{ gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Für diese Einheiten gibt es bereits einen Eigentümer – was passiert?</span>
              <div role="radiogroup" aria-label="Wechsel oder Miteigentum" className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
                {([
                  ['wechsel', 'Eigentümerwechsel', ArrowsLeftRight, wechselText(b.vorbesitzer, e.beginn)],
                  ['miteigentuemer', 'Miteigentümer', Users, 'Kommt hinzu, niemand gibt ab (z. B. Ehepartner, Erbengemeinschaft).'],
                ] as const).map(([key, label, IconKomp, text]) => {
                  const an = e.art === key;
                  return (
                    <button key={key} type="button" role="radio" aria-checked={an} onClick={() => setze('art')(key)}
                      className="flex text-left cursor-pointer" style={{ ...KACHEL(an), borderRadius: 16, padding: 12, gap: 10 }}>
                      <IconKomp weight="fill" size={20} color={an ? 'var(--acc)' : 'var(--ink3)'} style={{ flexShrink: 0 }} />
                      <span className="flex flex-col" style={{ gap: 2 }}>
                        <span style={{ fontSize: 13, fontWeight: 800 }}>{label}</span>
                        <span style={{ fontSize: 11, color: 'var(--ink3)', lineHeight: 1.35 }}>{text}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ),
    },
    {
      titel: 'Verwaltervertrag & Bank',
      untertitel: 'Vertragsbeginn, Verwaltungsart, Vergütung, Bankverbindung',
      erledigt: s3,
      inhalt: (
        <>
          <FeldRaster minBreite={170}>
            <TextFeld label="Vertragsbeginn / Stichtag" icon={CalendarBlank} type="date" wert={e.beginn} onChange={setze('beginn')} />
            <TextFeld label="Vertragsende (optional)" icon={CalendarX} type="date" wert={e.ende} onChange={setze('ende')} fehler={fehler.ende} />
            <TextFeld label="IBAN (Auszahlung)" icon={Bank} wert={e.iban} onChange={setze('iban')} placeholder="DE00 0000 …" autoComplete="off" fehler={fehler.iban} />
            <TextFeld label="Kontoinhaber" icon={IdentificationCard} wert={e.kontoinhaber} onChange={setze('kontoinhaber')} placeholder="falls abweichend vom Eigentümer" autoComplete="off" />
            <TextFeld label="Steuer-ID" icon={Hash} inputMode="numeric" wert={e.steuerId} onChange={setze('steuerId')} placeholder="11-stellig" autoComplete="off" fehler={fehler.steuerId} />
            <TextFeld label="Steuer-Nr." icon={Hash} wert={e.steuerNr} onChange={setze('steuerNr')} placeholder="z. B. 031 245 12345" autoComplete="off" />
            <TextFeld label="Notizen / Zusatzinfos" icon={NotePencil} wert={e.notizen} onChange={setze('notizen')} placeholder="Zusätzliche Vereinbarungen oder Informationen …" voll />
          </FeldRaster>
          <div className="flex items-center justify-between flex-wrap" style={{ gap: 12, padding: '12px 14px 12px 16px', borderRadius: 16, background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
            <span className="flex items-center" style={{ gap: 10, fontSize: 14, fontWeight: 700 }}><Briefcase size={18} color="var(--ink3)" />Vergütung</span>
            {objekt && mitVerguetung(rollen) ? (
              <Stepper label="Verwaltervergütung je Einheit" wert={e.verguetung} onChange={setze('verguetung')} min={0} max={999}
                einheit={`€ je Einheit · ${eur(verguetungMonat, { nachkomma: 0 })} / Monat`} />
            ) : (
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink2)' }}>
                {objekt ? 'keine – WEG-Vergütung über Hausgeld' : 'ergibt sich aus der Rolle'}
              </span>
            )}
          </div>
          <div className="flex flex-col" style={{ gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Rolle(n) – Mehrfachauswahl möglich</span>
            {objekt ? (
              <div className="flex flex-wrap self-start" style={{ gap: 4, padding: 3, borderRadius: 16, background: 'var(--fill2)' }}>
                {rollenOptionen(art).map(({ rolle, fest }) => {
                  const an = rollen.includes(rolle);
                  return (
                    <button key={rolle} type="button" aria-pressed={an} disabled={fest}
                      title={fest ? (art === 'WEG' ? 'In einer von uns verwalteten WEG ist jeder Eigentümer WEG-Mitglied' : 'Ergibt sich aus der Verwaltungsart des Objekts') : undefined}
                      onClick={() => setze('sev')(!e.sev)}
                      className={fest ? 'cursor-default' : 'cursor-pointer'}
                      style={{
                        height: 34, padding: '0 14px', borderRadius: 13, border: 'none', fontSize: 13, fontWeight: 700,
                        background: an ? 'var(--bg)' : 'transparent', color: an ? 'var(--ink)' : 'var(--ink2)', boxShadow: an ? '0 2px 8px -2px var(--shade)' : 'none',
                      }}>
                      {an ? `✓ ${ROLLEN_TEXT[rolle]}` : ROLLEN_TEXT[rolle]}
                    </button>
                  );
                })}
              </div>
            ) : (
              <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Ergibt sich aus dem gewählten Objekt.</span>
            )}
          </div>
          <SchalterZeile an={false} onChange={() => undefined} disabled icon={DeviceMobile} label="Einladung ins Eigentümerportal direkt senden"
            zusatz="Einladung per E-Mail wird noch angebunden – den Zugang danach beim Eigentümer freigeben" />
        </>
      ),
    },
  ];

  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <StepForm
        schritte={schritte}
        hinweis={h.text}
        bereit={h.bereit}
        speichert={speichert}
        onSpeichern={() => { void speichere(); }}
        speichernLabel={speichert ? 'Wird angelegt …' : 'Eigentümer anlegen'}
        vorschau={(
          <LivePreviewCard>
            <div className="flex items-center min-w-0" style={{ gap: 12 }}>
              <PersonAvatar text={s1 ? initialen(name) : '?'} ton={firma ? 'info' : 'acc'} />
              <div className="flex flex-col flex-1 min-w-0" style={{ gap: 1 }}>
                <span className="truncate" style={{ fontSize: 16, fontWeight: 800 }}>{name}</span>
                <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{umfang}</span>
              </div>
            </div>
            <div className="flex flex-wrap" style={{ gap: 6 }}>
              {e.beginn && e.beginn > heute ? <Chip ton="info">Übernahme geplant</Chip> : <Chip ton="ok">Aktiv</Chip>}
              {belegt && <Chip ton={e.art === 'wechsel' ? 'warn' : 'neutral'}>{e.art === 'wechsel' ? 'Eigentümerwechsel' : 'Miteigentümer'}</Chip>}
            </div>
            <div className="flex flex-col" style={{ gap: 8, paddingTop: 12, borderTop: '1px solid var(--line)', fontSize: 13 }}>
              <div className="flex justify-between"><span style={{ color: 'var(--ink2)' }}>Mandat ab</span><b className="immo-zahl">{e.beginn ? deDatum(e.beginn) : '–'}</b></div>
              <div className="flex justify-between"><span style={{ color: 'var(--ink2)' }}>Vergütung</span><b className="immo-zahl">{verguetungMonat > 0 ? `${eur(verguetungMonat, { nachkomma: 0 })} / Mon.` : '–'}</b></div>
              <div className="flex justify-between items-baseline" style={{ gap: 12 }}>
                <span style={{ color: 'var(--ink2)' }}>Verwaltung</span>
                <b className="text-right" style={{ fontSize: 15, color: 'var(--ok)' }}>{objekt ? rollen.map(r => ROLLEN_TEXT[r]).join(' + ') : '–'}</b>
              </div>
            </div>
            <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Eigentümerportal: Zugang nach dem Anlegen beim Eigentümer freigeben.</span>
          </LivePreviewCard>
        )}
      />
    </div>
  );
}
